import { Form, Flex, Input, Button, Checkbox, List } from "antd";

import { useMemo, createContext } from "react";

import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import PlusIcon from "@/icons/PlusIcon";
import TrashIcon from "@/icons/TrashIcon";

import DragHandle from "@/components/ui/DragHandle";

const SortableListItemContext = createContext({});

const TableColumnsList = ({ fieldName }) => {
  const SortableListItem = (props) => {
    const { itemKey, style, ...rest } = props;
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: itemKey });
    const listStyle = {
      ...style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
    };
    const memoizedValue = useMemo(
      () => ({ setActivatorNodeRef, listeners, attributes }),
      [setActivatorNodeRef, listeners, attributes]
    );
    return (
      <SortableListItemContext.Provider value={memoizedValue}>
        <List.Item {...rest} ref={setNodeRef} style={listStyle} />
      </SortableListItemContext.Provider>
    );
  };

  return (
    <Form.List name={[fieldName, "columns"]}>
      {(fields, { add, remove, move }) => (
        <>
          {fields.length > 0 && (
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={({ active, over }) => {
                if (!over || active.id === over.id) return;

                const oldIndex = fields.findIndex((f) => f.key === active.id);
                const newIndex = fields.findIndex((f) => f.key === over.id);
                if (oldIndex === -1 || newIndex === -1) return;

                move(oldIndex, newIndex);
              }}
              id="columns-drag-sorting"
            >
              <SortableContext
                items={fields.map((item) => item.key)}
                strategy={verticalListSortingStrategy}
              >
                <List
                  grid={{ gutter: 10, column: 1 }}
                  dataSource={fields}
                  renderItem={({ key, name: idx, ...restField }) => {
                    return (
                      <SortableListItem key={key} itemKey={key}>
                        <Flex align="start" gap={10} justify="space-between">
                          <Flex align="start" gap={10}>
                            <DragHandle
                              context={SortableListItemContext}
                              style={{ alignSelf: "center" }}
                            />
                            <Form.Item
                              {...restField}
                              name={[idx, "name"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Введите заголовок колонки",
                                },
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="Заголовок колонки" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[idx, "sumValues"]}
                              valuePropName="checked"
                              style={{ marginBottom: 0 }}
                            >
                              <Checkbox>Суммировать значения</Checkbox>
                            </Form.Item>
                          </Flex>
                          <Button
                            type="text"
                            icon={<TrashIcon />}
                            onClick={() => remove(idx)}
                          ></Button>
                        </Flex>
                      </SortableListItem>
                    );
                  }}
                ></List>
              </SortableContext>
            </DndContext>
          )}

          <Button
            type="dashed"
            icon={<PlusIcon />}
            aria-label="Добавить колонку"
            onClick={() => add({ name: "", sumValues: false })}
          >
            Добавить колонку
          </Button>
        </>
      )}
    </Form.List>
  );
};

export default TableColumnsList;
